import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

/**
 * Talks to the backend about direct uploads to Bunny Stream.
 *
 * The backend never receives the video itself. It creates an empty video in the
 * library and signs permission for this browser to fill it; the bytes then go straight
 * from here to Bunny. That is what makes a two-hour 4K film practical - routing tens of
 * gigabytes through the application server would exhaust it, and could not resume after
 * a dropped connection.
 */

export interface UploadAuthorisation {
  videoId: string;
  libraryId: string;
  /** Unix seconds. After this the signature is refused and a resume must re-authorise. */
  expiresAt: number;
  signature: string;
  endpoint: string;
}

export interface EncodingStatus {
  status: number;
  state: string;
  /** True only when every resolution is done - not merely when the video is playable. */
  finished: boolean;
  failed: boolean;
  encodeProgress: number | null;
  availableResolutions: string | null;
  lengthSeconds: number | null;
}

/** Thrown when the server has no Bunny Stream configuration, so the caller can fall back. */
export class StreamUnavailableError extends Error {
  constructor() {
    super('Bunny Stream is not configured on this server.');
    this.name = 'StreamUnavailableError';
  }
}

function adminUrl(path: string): string {
  // Same rule as adminService.updateContent: the configured base may or may not
  // already end in /api, and doubling it produces a 404 that looks like a missing
  // endpoint rather than a malformed URL.
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  return base.endsWith('/api') ? `${base}/admin/stream${path}` : `${base}/api/admin/stream${path}`;
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.message || `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

export async function authoriseUpload(title: string): Promise<UploadAuthorisation> {
  const body = new URLSearchParams({ title });
  const response = await unifiedAuth.fetch(adminUrl('/uploads'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (response.status === 503) throw new StreamUnavailableError();
  if (!response.ok) throw new Error(await readError(response));

  const json = await response.json();
  return (json.data ?? json) as UploadAuthorisation;
}

export async function getEncodingStatus(videoId: string): Promise<EncodingStatus> {
  const response = await unifiedAuth.fetch(adminUrl(`/videos/${encodeURIComponent(videoId)}`), {
    method: 'GET',
  });

  if (response.status === 503) throw new StreamUnavailableError();
  if (!response.ok) throw new Error(await readError(response));

  const json = await response.json();
  return (json.data ?? json) as EncodingStatus;
}
